import { CAN_I_EDIT_PRODUCTS_IN_BULK_ID } from "@faire/help-center-utils/articleIds";
import { Button } from "@faire/web--source/slate/Button";
import { Column } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import brandPortalProducts from "@faire/web-api--source/routes/brand-portal/my-shop/products";
import supportArticle from "@faire/web-api--source/routes/support/articles/articleId";
import React, { JSX } from "react";

import { Bold } from "../common/Bold";
import { LiItem } from "../common/LiItem";

export const EditMultipleProductsIA = () => {
  return (
    <Column>
      <Typography>
        <StrictLocalMsg
          defaultMessage="To edit multiple products at once, follow these steps:"
          description={{
            text: "Introduction to editing multiple products",
          }}
        />
      </Typography>
      <Spacer height="1x" />
      <ol>
        <LiItem step={1}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Navigate to your <LinkFormatting>Products</LinkFormatting> page."
              values={{
                LinkFormatting: ProductsLinkFormatting,
              }}
              description={{
                text: "First step to edit multiple products",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={2}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="To edit specific products, select 1+ products from any tab on the products table using the checkboxes next to the product images. To edit all products, you do not need to make a selection."
              description={{
                text: "Second step to edit multiple products",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={3}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Once any selections have been made, navigate to the toolbar menu in the middle of the page and click the <BoldFormatting>Bulk edit</BoldFormatting> link (with the pencil icon)."
              values={{
                BoldFormatting,
              }}
              description={{
                text: "Third step to edit multiple products",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={4}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="In the bulk edit view, you can edit basic information, selling methods, measurements, pricing, product details, settings, or inventory details (such as SKU numbers and tariff codes)."
              description={{
                text: "Fourth step to edit multiple products",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={5}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="To manage which columns you see, select <BoldFormatting>Change columns</BoldFormatting> and choose the columns you want to see and edit. Select the <BoldFormatting>Sort</BoldFormatting> option to organize products alphabetically or by publishing date. Use the <BoldFormatting>Search</BoldFormatting> icon (magnifying glass) to find specific products."
              values={{
                BoldFormatting,
              }}
              description={{
                text: "Fifth step to edit multiple products",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={6}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Required updates will be flagged with a red marker in the cells with issues."
              description={{
                text: "Sixth step to edit multiple products",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={7}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Once you’ve made changes, select the <BoldFormatting>Save edits</BoldFormatting> button in the top right corner to confirm."
              values={{
                BoldFormatting,
              }}
              description={{
                text: "Seventh step to edit multiple products",
              }}
            />
          </Typography>
        </LiItem>
      </ol>
      <Spacer height="1x" />
      <Typography>
        <StrictLocalMsg
          defaultMessage="For more information, visit <LinkFormatting>How to Bulk Edit Products</LinkFormatting>."
          values={{
            LinkFormatting: ArticleLinkFormatting,
          }}
          description={{
            text: "Link to bulk edit products",
          }}
        />
      </Typography>
    </Column>
  );
};

const ProductsLinkFormatting = (chunks: JSX.Element) => (
  <Button variant="tertiary" linkTo={brandPortalProducts()} target="_blank">
    <Bold>{chunks}</Bold>
  </Button>
);

const ArticleLinkFormatting = (chunks: JSX.Element) => (
  <Button
    variant="tertiary"
    linkTo={supportArticle(CAN_I_EDIT_PRODUCTS_IN_BULK_ID)}
    target="_blank"
  >
    {chunks}
  </Button>
);

const BoldFormatting = (chunks: JSX.Element) => <Bold>{chunks}</Bold>;
